/**
 * Event names and property bags for the export lifecycle.
 *
 * Two trigger families cover the same three phases: TPOGEN-004/005/006 for the
 * Plan Compare export and TPOGEN-069/070/071 for the plan reports. They differ
 * only in name and in whether they carry `plan_count` or `plan_id`, so the
 * choice is made here rather than at each of the two call sites — the modal
 * fires `started`, and the poller, which outlives it, fires the other two.
 */
import {
    TPOGEN_COMPARE_EXPORT_COMPLETED,
    TPOGEN_COMPARE_EXPORT_FAILED,
    TPOGEN_COMPARE_EXPORT_STARTED,
    TPOGEN_EXPORT_COMPLETED,
    TPOGEN_EXPORT_FAILED,
    TPOGEN_EXPORT_STARTED,
} from 'utils/mixpanelEventNames';

import { TPOGEN_PAGE_NAMES, type TpoGenPageName } from '../../utils/trackTpoGenEvent';
import { EXPORT_FORMAT } from '../config/exportConstants';
import type { ExportScope, ReportKind } from '../types';

type ExportPhase = 'started' | 'completed' | 'failed';

/**
 * The poller outlives the view that started the job, so `page_name` cannot be
 * read from the route. Every report kind belongs to exactly one surface, which
 * makes the kind a sound stand-in.
 */
export const REPORT_KIND_PAGE_NAMES: Record<ReportKind, TpoGenPageName> = {
    calendar: TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR,
    plan_metric: TPOGEN_PAGE_NAMES.PLAN_DETAIL_METRICS,
    promo_metric: TPOGEN_PAGE_NAMES.PLAN_DETAIL_METRICS,
    plan_comparison: TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON,
};

const COMPARISON_EVENTS: Record<ExportPhase, string> = {
    started: TPOGEN_COMPARE_EXPORT_STARTED,
    completed: TPOGEN_COMPARE_EXPORT_COMPLETED,
    failed: TPOGEN_COMPARE_EXPORT_FAILED,
};

const PLAN_EVENTS: Record<ExportPhase, string> = {
    started: TPOGEN_EXPORT_STARTED,
    completed: TPOGEN_EXPORT_COMPLETED,
    failed: TPOGEN_EXPORT_FAILED,
};

export interface ExportEventContext {
    reportKind: ReportKind;
    scope: ExportScope;
    /** Plan the export was addressed to; the base plan for `plan_comparison`. */
    planId: string;
    /** `plan_comparison` only: size of the comparison set. */
    planCount?: number;
}

export const exportEventName = (reportKind: ReportKind, phase: ExportPhase): string =>
    (reportKind === 'plan_comparison' ? COMPARISON_EVENTS : PLAN_EVENTS)[phase];

export const buildExportEventProps = (
    { reportKind, scope, planId, planCount }: ExportEventContext,
    extra: { durationMs?: number; failureReason?: string } = {},
): Record<string, unknown> => ({
    format: EXPORT_FORMAT,
    // `scope` never reaches the backend, so analytics is the only place the
    // user's current-view / all-data choice is recorded at all.
    report_kind: reportKind,
    scope,
    ...(reportKind === 'plan_comparison' ? { plan_count: planCount } : { plan_id: planId }),
    ...(extra.durationMs === undefined ? {} : { duration_ms: extra.durationMs }),
    ...(extra.failureReason ? { failure_reason: extra.failureReason } : {}),
});
